import { createApi } from '@reduxjs/toolkit/query/react';
import { getHeaders } from 'auth/auth';
import { baseQueryWithReauth } from '../baseQuery';
import { borrowerProfileAPI } from '../borrower-profile';

// RTK Query service for Related Applications (PROD-243) — the borrower's
// other in-flight FundMore applications, matched by Client ID, each
// includable as a READ-ONLY liability on the current application.
//
// Backend routes (org-scoped, standard auth; see the PROD-243 FE handoff):
//   GET    /b2b/loans/applications/{id}/related
//          → { synced, degraded, count, applications: [...] }
//          Also runs the lazy auto-remove reconcile server-side.
//   POST   /b2b/loans/applications/{id}/related/{relatedId}/liability
//          → "include" — creates the read-only liability row. Idempotent.
//   DELETE /b2b/loans/applications/{id}/related/{relatedId}/liability
//          → "remove". 409 on locked rows (Conditional Approval+).
//
// Both mutations return the SAME refreshed envelope as the GET — we write
// it straight into the list cache (no second fetch), then poke the
// borrower-profile summary (TDS) so the figures recalculate immediately
// (handoff §3.5).
//
// Divergence from manulife_frontend_v2: this app has no exposureAPI slice
// (the TotalExposure aggregates widget is Manulife-only), so only the
// borrower-profile invalidation runs here.

const invalidateRecalcFigures = (dispatch) => {
  dispatch(borrowerProfileAPI.util.invalidateTags(['getApplication']));
};

export const relatedApplicationsAPI = createApi({
  reducerPath: 'related-applications',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['RelatedApplications'],
  endpoints: (builder) => ({
    getRelatedApplications: builder.query({
      query: ({ applicationId }) => ({
        url: `/b2b/loans/applications/${applicationId}/related`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: (result, error, { applicationId }) => [
        { type: 'RelatedApplications', id: applicationId },
      ],
      // The GET reconciles auto-removals server-side; refresh the derived
      // figures whenever a fresh list lands (handoff §3.6 — simplest
      // correct version).
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          invalidateRecalcFigures(dispatch);
        } catch {
          /* list errors surface via the query state */
        }
      },
    }),

    includeRelatedApplication: builder.mutation({
      query: ({ applicationId, relatedApplicationId }) => ({
        url: `/b2b/loans/applications/${applicationId}/related/${relatedApplicationId}/liability`,
        method: 'POST',
        headers: getHeaders(),
        // No body by design — the backend derives every liability value.
      }),
      async onQueryStarted({ applicationId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            relatedApplicationsAPI.util.updateQueryData(
              'getRelatedApplications',
              { applicationId },
              () => data,
            ),
          );
          invalidateRecalcFigures(dispatch);
        } catch {
          /* errors handled by the caller (toast per status) */
        }
      },
    }),

    removeRelatedApplication: builder.mutation({
      query: ({ applicationId, relatedApplicationId }) => ({
        url: `/b2b/loans/applications/${applicationId}/related/${relatedApplicationId}/liability`,
        method: 'DELETE',
        headers: getHeaders(),
      }),
      async onQueryStarted({ applicationId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            relatedApplicationsAPI.util.updateQueryData(
              'getRelatedApplications',
              { applicationId },
              () => data,
            ),
          );
          invalidateRecalcFigures(dispatch);
        } catch {
          /* errors handled by the caller (toast per status) */
        }
      },
    }),
  }),
});

export const {
  useGetRelatedApplicationsQuery,
  useLazyGetRelatedApplicationsQuery,
  useIncludeRelatedApplicationMutation,
  useRemoveRelatedApplicationMutation,
} = relatedApplicationsAPI;
